/**
 * Listing CATEGORY discriminator — single source of truth.
 *
 * Wave 0 of `.tondocs/listings/NEW_CATEGORIES_PLAN.md` (§3.1): this tuple is
 * THE canonical declaration of "which product vertical is this listing".
 * Web (`types/listing.ts`), mobile (`apps/mobile/components/sell-flow/types.ts`),
 * and every feed-filter re-export from here — no local literal unions.
 *
 * NOT the same axis as:
 *  - `ListingFormat` (sale model — fixed_price/auction/…, `listing-adt.ts`)
 *  - `CardPackagingFormat` (trading-card packaging — single/slab/…)
 *
 * Extension protocol (enforced by `tests/unit/fitness-listing-category.test.ts`):
 * every branch on this axis must be an exhaustive `switch` ending in
 * `default: unreachableCategory(x)` or a `Record<ListingCategory, …>` lookup,
 * so that appending a value here turns every unhandled branch into a compile
 * error. Convert branches FIRST (at the current cardinality), widen SECOND.
 *
 * On the wire the discriminator is still the legacy-named `cardCategory`
 * field (absent ≡ "lego") — decision D1 2026-07-08 keeps the misnomer and
 * widens its literal union per category; the explicit `category` field rename
 * is deferred structural cleanup (plan §3.2).
 */
export const LISTING_CATEGORIES = ["lego", "trading_card", "comic"] as const;
export type ListingCategory = (typeof LISTING_CATEGORIES)[number];

/**
 * Exhaustiveness probe. `switch (cat) { … default: return unreachableCategory(cat) }`
 * — compiles only while every member of `LISTING_CATEGORIES` has a case.
 */
export function unreachableCategory(x: never): never {
  throw new Error(`Unhandled ListingCategory: ${String(x)}`);
}

/** Boundary parser — total function, `null` for anything off the tuple. */
export function parseListingCategory(raw: unknown): ListingCategory | null {
  return typeof raw === "string" &&
    (LISTING_CATEGORIES as readonly string[]).includes(raw)
    ? (raw as ListingCategory)
    : null;
}
